import * as React from 'react';
import { UncontrolledAlert } from 'reactstrap';
import * as H from 'history';
import { APIError } from '../../../APIError';
import {
  getOrigin,
  HTTP_STATUS,
  sortURLSearchParams,
  unionStringArrays,
} from '../../../constants';
import ErrorJumbotron from '../../../widgets/ErrorJumbotron';
import Icon from '../../../widgets/Icons';
import LoadingIndicator from '../../../widgets/LoadingIndicator';
import Panel from '../../../widgets/Panel';
import ViewHeader from '../../../widgets/ViewHeader';
import Viewport from '../../../widgets/Viewport';
import withFilterManager, {
  InjectedProps as FilterManagerInjectedProps,
} from '../../FilterManager';
import AdditionalFilterBar, {
  FilterDropdown,
} from '../common/filterbar/AdditionalFilterBar';
import ViewTypeBar from '../common/filterbar/ViewTypeBar';
import './BaseDataView.scss';
import { PageTitle } from '../../../widgets/PageTitle';
import { ShareLink } from '../../contact/ShareLink';
import { getUrlDateRangeLocal } from './data_constants';
import { apiRawFetch } from '../../../APIClient';
import DataResultTable, { DataColumn } from './DataResultTable';
import { Choice } from '../../../widgets/ChoiceSelector';
import DataMap from './DataMap';
import { Validator } from '../../filterValidators';
import { AdditionalPivots } from './PivotButton';
import makeComponentTrashable, { TrashableProps } from 'trashable-react';

export type BaseDataEntry = {
  [key: string]: any;
  name: string;
  code?: string;
};

export type BaseDataResult<D extends BaseDataEntry> = {
  readonly results: Array<D>;
  readonly count: number;
  readonly start: string;
  readonly end: string;
};

type OwnProps<D extends BaseDataEntry, V extends string> = {
  title: string;
  mapTitle?: string;
  route: string;
  endpoint: string;
  processData?: (data: BaseDataResult<D>) => BaseDataResult<D>;
  history: H.History;
  viewChoices: Array<Choice>;
  type: V;
  filterKeys: Array<string>;
  seenKeys: Array<string>;
  initialSeen?: SeenDict;
  columns: Array<DataColumn>;
  defaultSort: string;
  filterDropdowns: Array<FilterDropdown>;
  mapViews?: Array<V>;
  mapCountField?: string;
  additionalPivotOptions?: Array<AdditionalPivots>;
};

export type Props<D extends BaseDataEntry, V extends string> = OwnProps<D, V> &
  FilterManagerInjectedProps &
  TrashableProps;

export type State<D extends BaseDataEntry> = {
  adjustment: string | null;
  data: Array<D>;
  error: APIError | null;
  isLoading: boolean;
  seen: SeenDict;
};

export type SeenDict = {
  [Key: string]: Array<string>;
};

const NO_RESULTS = new APIError(
  'No results found matching your search criteria',
  HTTP_STATUS.NOT_FOUND,
);

export class BaseDataView<
  D extends BaseDataEntry,
  V extends string,
> extends React.Component<Props<D, V>, State<D>> {
  constructor(props: Props<D, V>) {
    super(props);
    this.state = {
      adjustment: null,
      data: [],
      error: null,
      isLoading: false,
      seen: props.initialSeen ? props.initialSeen : {},
    };
  }

  componentDidMount() {
    this.fetchApiData();
  }

  componentDidUpdate(prevProps: Props<D, V>, prevState: State<D>) {
    const { type: route, filters, initialSeen } = this.props;
    const {
      type: prevRoute,
      filters: prevFilters,
      initialSeen: prevInitialSeen,
    } = prevProps;

    const { adjustment } = this.state;
    const { adjustment: prevAdjustment } = prevState;

    const routeChanged = prevRoute !== route;
    const oldFilters = new URLSearchParams(prevFilters.toString());
    const newFilters = new URLSearchParams(filters.toString());
    let filtersUnchanged = oldFilters.toString() === newFilters.toString();

    if (initialSeen !== prevInitialSeen) {
      this.setState((prevState) => ({
        seen: Object.assign({}, prevState.seen, initialSeen),
      }));
    }

    // special case: if only the date filters changed and an adjustment was made by
    // backend, then don't bother to re-fetch the data (it will be the same)
    oldFilters.delete('start');
    oldFilters.delete('end');
    newFilters.delete('start');
    newFilters.delete('end');
    if (
      oldFilters.toString() === newFilters.toString() &&
      !prevAdjustment &&
      adjustment
    ) {
      filtersUnchanged = true;
    }
    if (!filtersUnchanged || routeChanged) {
      // the view or filters changed
      this.fetchApiData();
    }
  }

  fetchApiData() {
    const { type, endpoint, filters, registerPromise } = this.props;
    this.setState({ isLoading: true });
    registerPromise(
      apiRawFetch.getApiData<BaseDataResult<D>>(type, filters, endpoint),
    ).then(
      (resp) => {
        this.updateData(resp);
      },
      (err) => {
        this.setState({ isLoading: false, error: err });
      },
    );
  }

  updateData(data: BaseDataResult<D>) {
    const { type, filters: currentFilters, seenKeys, processData } = this.props;

    const metadata: {
      adjustment: string | null;
      error: APIError | null;
      seen?: SeenDict;
      filters?: Array<{ key: string; value: string | null }>;
    } = {
      adjustment: null,
      error: null,
    }; // Add the results we've fetched to our 'seen' history for dropdowns if appropriate

    if (processData) {
      data = processData(data);
    }

    if (seenKeys.includes(type)) {
      const newSeen: SeenDict = {};
      const values = data.results.map((result) => result.name);
      seenKeys.forEach((k) => {
        if (this.state.seen[k]) {
          newSeen[k] = this.state.seen[k];
        }
        if (type === k) {
          newSeen[k] = unionStringArrays(this.state.seen[k] || [], values);
        }
      });
      metadata.seen = newSeen;
    } // See if we should make and note any adjustments to time range

    const currentRange = getUrlDateRangeLocal(
      currentFilters.get('start'),
      currentFilters.get('end'),
    );
    const newRange = getUrlDateRangeLocal(data.start, data.end);
    let msg = [];

    if (currentRange.start !== newRange.start) {
      msg.push('start date');
    }

    if (currentRange.end !== newRange.end) {
      msg.push('end date');
    }

    if (msg.length > 0) {
      metadata.filters = [
        { key: 'start', value: newRange.start },
        { key: 'end', value: newRange.end },
      ];
      metadata.adjustment = `The ${msg.join(' and ')} of your results ${
        msg.length === 1 ? 'was' : 'were'
      } adjusted to fall within the available range.`;
    }

    if (metadata.filters) this.props.setFilterValues(metadata.filters);
    this.setState((prevState) => ({
      ...prevState,
      isLoading: false,
      data: data.results,
      ...metadata,
    }));
  }

  /**
   * Changes the current view (URL) to the one specified, adding the current URL search params
   * @param {string} view - the desired view type (e.g. 'device')
   */
  changeViewTo = (view: string) => {
    this.props.history.push(view + '?' + this.props.filters.toString());
  };

  render() {
    const {
      title,
      mapTitle,
      route,
      history,
      setFilterValues,
      viewChoices,
      type,
      filters,
      filterKeys,
      filterDropdowns,
      columns,
      defaultSort,
      mapViews,
      mapCountField,
      additionalPivotOptions,
    } = this.props;

    const { adjustment, data, error, isLoading, seen } = this.state;

    let content, map;
    if (isLoading) {
      content = (
        <LoadingIndicator size="lg" centered>
          Loading results...
        </LoadingIndicator>
      );
    } else if (error) {
      content = (
        <ErrorJumbotron error={error} className="jumbotron-no-high-z" />
      );
    } else if (data.length === 0) {
      content = (
        <ErrorJumbotron error={NO_RESULTS} className="jumbotron-no-high-z" />
      );
    } else {
      if (mapViews?.includes(type)) {
        map = (
          <Panel title={mapTitle!}>
            <DataMap data={data} countField={mapCountField!} />
          </Panel>
        );
      }
      content = (
        <DataResultTable
          columns={columns}
          data={data}
          filters={filters}
          onViewChange={this.changeViewTo}
          setFilterValues={setFilterValues}
          type={type}
          viewChoices={viewChoices}
          defaultSort={defaultSort}
          additionalPivotOptions={additionalPivotOptions}
        />
      );
    }

    return (
      <Viewport>
        <PageTitle title={title} />
        <ViewHeader title={title} />
        <Panel className="pt-0">
          <ViewTypeBar
            title="Group Data By"
            filters={filters}
            history={history}
            choices={viewChoices}
            onViewChange={this.changeViewTo}
            setFilterValues={setFilterValues}
            type={type}
          />
          {adjustment && (
            <UncontrolledAlert
              color="warning"
              className="text-center mt-4 mb-0"
            >
              {adjustment}
            </UncontrolledAlert>
          )}
        </Panel>
        <Panel className="filter-bar">
          <AdditionalFilterBar
            filterKeys={filterKeys}
            filters={filters}
            history={history}
            setFilterValues={setFilterValues}
            filterDropdowns={filterDropdowns}
            seen={seen}
          />
        </Panel>
        <div className="d-flex justify-content-between my-prs">
          <div className="search-results-text">
            <h3>
              <span style={{ textTransform: 'capitalize' }}>{
                `${type.replace(
                  '_',
                  ' ',
                )}s ` /* TODO: this needs to be a lookup for i18n */
              }</span>
              {!isLoading && data && (
                <small>
                  <i>
                    {data.length > 100 ? (
                      <span>(Top 100)</span>
                    ) : (
                      <span>({data.length})</span>
                    )}
                  </i>
                </small>
              )}
            </h3>
          </div>
          <div className="text-right font-italic">
            <ShareLink
              subject={`Question about ${type.replace('_', ' ')} ${title}`}
              link={`${getOrigin()}${route}/${type}?${filters.toString()}`}
            >
              Ask your CST about this view
              <Icon.Share className="ml-2 mt-n1" />
            </ShareLink>
          </div>
        </div>
        {map}
        <Panel className="min-height-for-loading-indicator">{content}</Panel>
      </Viewport>
    );
  }
}

const TrashableBaseDataView = makeComponentTrashable(BaseDataView);
const FilterManagedBaseDataView = withFilterManager(TrashableBaseDataView);

export type ContainerProps<D extends BaseDataEntry, V extends string> = {
  validators: { [key: string]: Validator };
} & OwnProps<D, V>;

export type ContainerState = {
  filters: URLSearchParams | null;
  seen: SeenDict;
};

export default class BaseDataContainer<
  D extends BaseDataEntry,
  V extends string,
> extends React.Component<ContainerProps<D, V>, ContainerState> {
  constructor(props: ContainerProps<D, V>) {
    super(props);
    this.state = {
      filters: null,
      seen: props.initialSeen ? props.initialSeen : {},
    };
  }

  setFilters(filters: URLSearchParams) {
    const newFilter = sortURLSearchParams(filters, this.props.filterKeys);
    let seenFilters: SeenDict = {};
    this.props.seenKeys.forEach((k) => {
      if (this.state.seen[k]) {
        seenFilters[k] = (this.state.seen[k] || []).slice();
      }
      const value = newFilter.get(k);
      if (value) {
        seenFilters[k] = unionStringArrays(
          seenFilters[k] || [],
          value.split(','),
        );
      }
    });
    this.setState({ filters: filters, seen: seenFilters });
  }

  render() {
    const { ...props } = this.props;
    //@ts-ignore: TODO - properly type generics (see FilterManager.tsx)
    return (
      <FilterManagedBaseDataView
        setFilters={this.setFilters.bind(this)}
        filters={this.state.filters}
        initialSeen={this.state.seen}
        {...props}
      />
    );
  }
}
